import { FC } from "react";
import { useAtomValue } from "jotai";
import { positiveState, negativeState } from "@/lib/store";
import { QuarterlyStatistics } from "@/models/event-page";
import { STAT_WINDOWS, buildQuarterlyRows, formatPct } from "./helpers";
import styles from "./EventPageRevamp.module.scss";

const SPARK_MAX = 6; // % scale for the diverging spark bars

const Spark: FC<{ value: number | null | undefined; positive: string; negative: string }> = ({ value, positive, negative }) => {
    if (value == null || Number.isNaN(value)) return <span className={styles.spark} />;
    const pct = Math.min(Math.abs(value) / SPARK_MAX, 1) * 50;
    const isPos = value >= 0;
    return (
        <span className={styles.spark}>
            <span className={styles.sparkAxis} />
            <span
                className={styles.sparkFill}
                style={{
                    left: isPos ? "50%" : `${50 - pct}%`,
                    width: `${pct}%`,
                    background: isPos ? positive : negative,
                }}
            />
        </span>
    );
};

interface StatsTableProps {
    stats: QuarterlyStatistics | null | undefined;
}

/** Quarterly stats table: mean (+median) per window with a diverging spark bar. */
const StatsTable: FC<StatsTableProps> = ({ stats }) => {
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const rows = buildQuarterlyRows(stats);

    if (rows.length === 0) return <div className={styles.empty}>No earnings statistics available.</div>;

    return (
        <>
            <div className={styles.statsTableWrap}>
                <table className={styles.statsTable}>
                    <thead>
                        <tr>
                            <th>Event</th>
                            <th>Occur.</th>
                            {STAT_WINDOWS.map((w) => (
                                <th key={w.label}>{w.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.name} className={r.name === "Earnings Total" ? styles.total : ""}>
                                <td>{r.name}</td>
                                <td>{r.occurrences}</td>
                                {(r.cells ?? []).map((cell, j) => {
                                    const mean = cell?.avg ?? null;
                                    const median = cell?.median ?? null;
                                    return (
                                        <td key={j}>
                                            <div className={styles.statCellNums}>
                                                <span className={`${styles.statMean} ${mean != null && mean >= 0 ? styles.up : styles.down}`}>{formatPct(mean)}</span>
                                                <span className={styles.statMedian} title="Median">
                                                    med {formatPct(median)}
                                                </span>
                                            </div>
                                            <Spark value={mean} positive={positive} negative={negative} />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className={styles.tableLegend}>
                <span>mean return · med = median</span>
                <span>Returns measured vs the event date. Bars scaled to ±{SPARK_MAX}%.</span>
            </div>
        </>
    );
};

export default StatsTable;
